import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronRight, Share, Binoculars } from 'lucide-react';
import type { PitcherReadiness } from '../../lib/pitcher-readiness';
import type { BatterSeasonSignal } from '../../lib/insights/season-signals';
import { DEFENSE_MODES, ORDER_MODES } from '../../lib/batting-order';
import { WizardBar, WizardHead, WizardFoot, WizardList, TwoPane } from './WizardChrome';
import { DefenseGrid, RebuildDefenseSheet } from './DefenseGrid';
import { PlanShareSheet } from './StepReview';

// "Skip Wizard, Manually Plan": the whole plan on one screen, the way the
// Game Planner worked before the wizard (its Game Plan tab, commit f802cd9).
// Starting pitcher and the two autofills up top, then one grid that is both
// the batting order (drag the number) and every inning's defense.
//
// It is a view over the same state as the wizard, not a second planner: the
// grid swaps, the bullpen plan puts each arm on the mound, and saving writes
// the same plan document. Experienced coaches who know what they want skip
// five screens; everything the wizard knows still applies.

const Picker = ({ label, value, valueLabel, options, onChange, disabled }: {
  label: string;
  value: string;
  valueLabel: string;
  options: { key: string; label: string }[];
  onChange: (key: string) => void;
  disabled?: boolean;
}) => (
  <div className={`relative flex-1 min-w-0 px-4 py-3 ${disabled ? 'opacity-50' : ''}`}>
    <span className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-2">{label}</span>
    <span className="flex items-center gap-1 mt-0.5">
      <span className="flex-1 min-w-0 truncate text-[15px] font-bold text-ink">{valueLabel}</span>
      <span className="text-ink-mute text-xs shrink-0">▾</span>
    </span>
    <select
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      aria-label={label}
      className="absolute inset-0 w-full h-full opacity-0"
    >
      {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  </div>
);

export const ManualPlan = ({
  game, team, players, innings, numInnings, settings,
  presentCount, absentCount,
  showPitching, pitcherRows, selectedPitcherId, bullpenSummary,
  orderMode, batterSignals, defenseMode, defenseEdited, isGenerating,
  canBeginGame, hasScouting,
  onAvailability, onSelectPitcher, onBullpen, onApplyOrder, onDefenseMode,
  onReorder, onSetPosition, onCopyToLaterInnings, onAddInning, onRemoveInning,
  onPrintPlan, onPrintCard, onScouting, onBack, onBeginGame, onSaveAndExit, wide = false, side,
}: {
  game: any;
  team: any;
  /** Present players, in batting order. */
  players: any[];
  innings: any[];
  numInnings: number;
  settings: any;
  presentCount: number;
  absentCount: number;
  /** Players-pitch only: starter and bullpen rows. */
  showPitching: boolean;
  pitcherRows: PitcherReadiness[];
  selectedPitcherId: string | null;
  bullpenSummary: string | null;
  orderMode: string;
  batterSignals: Map<string, BatterSeasonSignal>;
  defenseMode: string;
  defenseEdited: boolean;
  isGenerating: boolean;
  canBeginGame: boolean;
  hasScouting: boolean;
  onAvailability: () => void;
  onSelectPitcher: (playerId: string) => void;
  onBullpen: () => void;
  onApplyOrder: (mode: string) => void;
  onDefenseMode: (mode: string) => void;
  onReorder: (players: any[]) => void;
  onSetPosition: (inningIdx: number, playerId: string, position: string) => void;
  onCopyToLaterInnings: (inningIdx: number) => void;
  onAddInning?: () => void;
  onRemoveInning?: () => void;
  onPrintPlan: () => void;
  onPrintCard: () => void;
  onScouting: () => void;
  onBack: () => void;
  onBeginGame: () => void;
  onSaveAndExit: () => void;
  /** Wide screens: the grid becomes the Lineup Wall and `side` fills the right pane. */
  wide?: boolean;
  side?: React.ReactNode;
}) => {
  const [pickingPitcher, setPickingPitcher] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const d = new Date(game.date);

  const starter = pitcherRows.find(r => r.player.id === selectedPitcherId)
    ?? (selectedPitcherId ? { player: players.find((p: any) => p.id === selectedPitcherId), statusLabel: '' } as any : null);
  const hasDefense = innings.slice(0, numInnings).some(inn => inn?.length > 0);

  // Same rule as the wizard's order step: the two modes built on imported
  // at-bats only appear once there are at-bats to build them from.
  // 'None' is the xOVR order, a real mode, not "nothing chosen".
  const orderOptions = (batterSignals.size > 0 ? ORDER_MODES : ORDER_MODES.filter(m => !m.data))
    .map(m => ({ key: m.key, label: m.label }));
  const orderLabel = orderOptions.find(o => o.key === orderMode)?.label ?? 'Choose';
  const defenseLabel = isGenerating
    ? 'Filling…'
    : DEFENSE_MODES.find(m => m.key === defenseMode)?.label ?? 'Choose';

  const handleMode = (mode: string) => {
    if (defenseEdited && hasDefense && mode !== defenseMode) setPendingMode(mode);
    else onDefenseMode(mode);
  };

  return (
    <TwoPane wide={wide} side={side}>
      <WizardBar
        onBack={onBack}
        right={
          <button onClick={() => setSharing(true)} aria-label="Print or share" className="p-1 -mr-1">
            <Share className="w-5 h-5" />
          </button>
        }
      />
      <WizardHead
        title={`${format(d, 'EEE')} ${game.isHome ? 'vs' : 'at'} ${game.opponent || 'Opponent'}`}
        sub={`${format(d, 'MMM d')}${game.time ? ` · ${game.time}` : ''} · ${game.isHome ? 'Home' : 'Away'}`}
      />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        <button
          onClick={onAvailability}
          className="w-full bg-surface rounded-card px-4 py-3 flex items-center gap-3 mb-3 text-left"
        >
          <span className="flex-1 text-[15px] font-semibold text-ink">Availability</span>
          <span className="text-[14px] text-ink-2 tabular-nums">
            {presentCount} in{absentCount > 0 && <>, <span className="text-danger font-bold">{absentCount} out</span></>}
          </span>
          <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
        </button>

        <div className="bg-surface rounded-card mb-4 overflow-hidden">
          {showPitching && (
            <>
              <button
                onClick={() => setPickingPitcher(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left border-b border-hairline"
              >
                <span className={`w-[38px] h-[38px] shrink-0 rounded-full flex items-center justify-center text-[13px] font-bold tabular-nums ${
                  starter ? 'bg-ink text-surface' : 'bg-raised text-ink-2'
                }`}>
                  {starter ? `#${starter.player?.number ?? ''}` : '?'}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-2">Starting pitcher</span>
                  <span className={`block text-[15.5px] font-bold truncate ${starter ? 'text-ink' : 'text-ink-2'}`}>
                    {starter ? starter.player?.name : 'Tap to choose'}
                  </span>
                  {starter?.status === 'rest' && (
                    <span className="block text-[12.5px] font-semibold text-gold-ink">{starter.statusLabel}</span>
                  )}
                </span>
                <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
              </button>
              <button
                onClick={onBullpen}
                className="w-full flex items-center gap-3 px-4 py-3 text-left border-b border-hairline"
              >
                <span className="text-[14px] text-ink-2 shrink-0">Bullpen</span>
                <span className="flex-1 min-w-0 text-right text-[14px] font-bold text-ink truncate">
                  {bullpenSummary ?? 'Plan relievers'}
                </span>
                <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
              </button>
            </>
          )}
          <div className="flex divide-x divide-hairline">
            <Picker
              label="Batting order"
              value={orderMode}
              valueLabel={orderLabel}
              options={orderOptions}
              onChange={onApplyOrder}
            />
            <Picker
              label="Defense"
              value={defenseMode}
              valueLabel={defenseLabel}
              options={DEFENSE_MODES.map(m => ({ key: m.key, label: m.label }))}
              onChange={handleMode}
              disabled={isGenerating}
            />
          </div>
        </div>

        {isGenerating && (
          <p className="text-sm text-ink-2 px-1 py-6 text-center">Working out the defense…</p>
        )}
        {!isGenerating && !hasDefense && (
          <p className="text-sm text-ink-2 px-1 py-6 text-center">Pick a Defense fill above to build every inning.</p>
        )}
        {!isGenerating && hasDefense && (
          <DefenseGrid
            innings={innings}
            numInnings={numInnings}
            players={players}
            settings={settings}
            onSetPosition={onSetPosition}
            onCopyToLaterInnings={onCopyToLaterInnings}
            onAddInning={onAddInning}
            onRemoveInning={onRemoveInning}
            onReorder={onReorder}
            wide={wide}
          />
        )}

        {hasScouting && (
          <button
            onClick={onScouting}
            className="w-full bg-surface rounded-card mt-4 flex items-center gap-3 px-4 py-3 text-left"
          >
            <Binoculars className="w-[17px] h-[17px] text-ink shrink-0" />
            <span className="flex-1 text-[14.5px] font-semibold text-ink">Opponent scouting</span>
            <ChevronRight className="w-4 h-4 text-ink-mute shrink-0" />
          </button>
        )}

        <div className="h-4" />
      </div>

      <WizardFoot
        primaryLabel={canBeginGame ? 'Begin Game' : 'Mark Ready'}
        onPrimary={onBeginGame}
        secondaryLabel="Save and Exit"
        onSecondary={onSaveAndExit}
        secondaryAsButton
      />

      {pickingPitcher && (
        <div className="absolute inset-0 z-30 flex flex-col">
          <button className="flex-1 bg-ink/30" onClick={() => setPickingPitcher(false)} aria-label="Dismiss" />
          <div className="bg-surface rounded-t-panel px-4 pt-5 pb-5 safe-bottom max-h-[70%] overflow-y-auto">
            <h3 className="text-[19px] font-bold tracking-[-0.02em] text-ink px-1 mb-3">Starting pitcher</h3>
            <WizardList className="border border-hairline">
              {pitcherRows.map((r, i) => (
                <button
                  key={r.player.id}
                  onClick={() => { setPickingPitcher(false); onSelectPitcher(r.player.id); }}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-left ${i === pitcherRows.length - 1 ? '' : 'border-b border-hairline'}`}
                >
                  <span className="w-[34px] shrink-0 text-[13px] font-semibold text-ink-2 tabular-nums">#{r.player.number}</span>
                  <span className={`flex-1 min-w-0 text-base truncate ${r.player.id === selectedPitcherId ? 'font-bold text-ink' : 'font-semibold text-ink'}`}>
                    {r.player.name}
                  </span>
                  <span className={`text-sm shrink-0 ${r.status === 'rest' ? 'text-gold-ink font-semibold' : 'text-ink-2'}`}>
                    {r.statusLabel}
                  </span>
                </button>
              ))}
            </WizardList>
            {pitcherRows.length === 0 && (
              <p className="text-sm text-ink-2 px-1">No pitchers on the roster yet. Tag pitchers in a player's positions.</p>
            )}
          </div>
        </div>
      )}

      {sharing && (
        <PlanShareSheet
          game={game}
          onPrintPlan={onPrintPlan}
          onPrintCard={onPrintCard}
          onClose={() => setSharing(false)}
        />
      )}

      <RebuildDefenseSheet
        mode={pendingMode}
        onConfirm={(m) => { setPendingMode(null); onDefenseMode(m); }}
        onCancel={() => setPendingMode(null)}
      />
    </TwoPane>
  );
};
